import { all, takeLatest, put, retry, select, call } from 'redux-saga/effects';
import { inviteActions, inviteSelector } from '$store/invite/index';
import { Api } from '$api';
import { batchActions } from '$store';
import { mainActions } from '$store/main';
import { toastActions } from '$store/toast';
import { hideModal, showModal } from '$navigation/helper';
import {
  CreateLinkAction,
  DeleteLinkAction,
  InviteLink,
  InviteState,
  InviteStatsRow,
  LoadMoreTypeAction,
} from '$store/invite/interface';
import { createInviteLink } from '$utils';

function* loadSuggestionsWorker() {
  try {
    const data = yield retry(Infinity, 3000, Api.post, '/invite/suggestions', {
      startFrom: 0,
    });
    yield put(
      batchActions(
        mainActions.setUsers(data.users),
        inviteActions.setSuggestions({
          suggestions: data.suggestions,
          nextFrom: data.nextFrom,
        }),
      ),
    );
  } catch (e) {}
}

function* loadMoreSuggestionsWorker() {
  try {
    const { suggestionsNextFrom } = yield select(inviteSelector);
    const data = yield call(Api.post, '/invite/suggestions', {
      startFrom: suggestionsNextFrom,
    });
    yield put(
      batchActions(
        mainActions.setUsers(data.users),
        inviteActions.setLoadedMoreSuggestions({
          suggestions: data.suggestions,
          nextFrom: data.nextFrom,
        }),
      ),
    );
  } catch (e) {}
}

export function* loadInvitesFriendsWorker() {
  try {
    const data = yield call(Api.get, '/invite/friends');
    yield put(
      batchActions(
        inviteActions.setInvitedFriends({
          friends: data.friends,
          total: data.friendsCount,
          bonusFansAdded: +data.bonusFansAdded,
          bonusFansEnabled: !!data.bonusFansEnabled,
          bonusFansNeed: +data.bonusFansNeed,
          invitedTotal: +data.invitedTotal,
          bonusMultiplier: +data.bonusMultiplier,
        }),
        inviteActions.setBonus(data.bonus),
        mainActions.setUsers(data.users),
        inviteActions.setMorePagination({
          type: 'friends',
          nextFrom: Number(data.nextFrom),
          length: data.friendsCount,
        }),
      ),
    );
  } catch (e) {
    yield put(
      batchActions(
        toastActions.setToastFail(e.message),
        inviteActions.loadInvitesFriendsFailed(),
      ),
    );
  }
}

function* loadInviteStatsWorker() {
  try {
    const data = yield call(Api.get, '/invite/stats');

    yield put(
      batchActions(
        mainActions.setUsers(data.users),
        inviteActions.setInviteStats(data),
        inviteActions.setMorePagination({
          type: 'stats',
          nextFrom: Number(data.nextFrom),
          length: data.friendsCount,
        }),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadMoreWorker(action: LoadMoreTypeAction) {
  const type = action.payload;
  const { pagination } = yield select(inviteSelector);

  try {
    if (pagination[type].nextFrom === 0) return;

    const data = yield call(
      Api.get,
      `/invite/${type === 'statsTable' ? 'stats/table' : type}`,
      {
        params: { startFrom: pagination[type].nextFrom },
      },
    );

    yield put(
      batchActions(
        mainActions.setUsers(data.users),
        inviteActions.stopLoadMore({
          type: type,
          nextFrom: Number(data.nextFrom),
        }),
      ),
    );

    switch (type) {
      case 'stats':
        yield put(inviteActions.setMoreInviteStats(data));
        break;
      case 'friends':
        yield put(inviteActions.setMoreInvitedFriends(data));
        break;
    }
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadMoreStatsTableWorker() {
  try {
    const type = 'statsTable';
    const { pagination } = yield select(inviteSelector);

    if (pagination[type].nextFrom === 0) return;

    const data = yield call(Api.post, `/invite/stats/table`, {
      startFrom: pagination[type].nextFrom,
    });

    yield put(
      batchActions(
        inviteActions.stopLoadMore({ type, nextFrom: Number(data.nextFrom) }),
        inviteActions.setMoreStatsTable(data),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* enableGoldWorker() {
  try {
    yield put(toastActions.setToastLoading());
    yield call(Api.post, '/invite/gold/enable');
    yield loadInviteStatsWorker();
    yield loadInviteStatsTableWorker();
    yield put(toastActions.setToastSuccess());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  } finally {
    yield call(hideModal);
  }
}

function* loadInviteStatsTableWorker() {
  try {
    const { statsDateRange } = (yield select(inviteSelector)) as InviteState;
    const [from, to] = statsDateRange.map((date) => date.getTime() / 1000);
    yield put(inviteActions.setStatsTableLoading(true));
    const response: {
      stats: InviteStatsRow[];
      links: InviteLink[];
      nextFrom: string;
      linksCount: number;
    } = yield call(Api.post, '/invite/stats/table', {
      linkIds: [],
      from,
      to,
    });
    yield put(
      batchActions(
        inviteActions.setMorePagination({
          type: 'statsTable',
          nextFrom: Number(response.nextFrom),
          length: response.linksCount,
        }),
        inviteActions.setInviteStatsTable(response),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  } finally {
    yield put(inviteActions.setStatsTableLoading(true));
  }
}

function* createLinkWorker(action: CreateLinkAction) {
  try {
    yield put(toastActions.setToastLoading());
    const res: {
      link: InviteLink;
    } = yield call(Api.post, '/invite/link/create', action.payload);
    const linkString = createInviteLink(
      res.link.link,
      action.payload.userId,
      res.link.id,
    );
    if (linkString) {
      yield put(inviteActions.addLink(res.link));
      yield call(showModal, 'links', true, {}, { highlight: 1 });
    }
    yield put(toastActions.hideToast());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  } finally {
  }
}

function* setStateDateRangeWorker() {
  yield put(toastActions.setToastLoading());
  yield loadInviteStatsTableWorker();
  yield put(toastActions.hideToast());
}

function* deleteLinkWorker(action: DeleteLinkAction) {
  const { links } = (yield select(inviteSelector)) as InviteState;
  const link = links?.find((l) => l.id === action.payload);
  try {
    yield put(inviteActions.deleteLink(action.payload));
    yield call(Api.post, `/invite/link/${action.payload}/delete`);
  } catch (e) {
    if (link) {
      yield put(inviteActions.addLink(link));
    }
    yield put(toastActions.setToastFail(e.message));
  }
}

export function* inviteSaga() {
  yield all([
    takeLatest(inviteActions.loadSuggestions, loadSuggestionsWorker),
    takeLatest(inviteActions.loadMoreSuggestions, loadMoreSuggestionsWorker),
    takeLatest(inviteActions.loadInvitesFriends, loadInvitesFriendsWorker),
    takeLatest(inviteActions.loadInviteStats, loadInviteStatsWorker),
    takeLatest(inviteActions.loadMoreStatsTable, loadMoreStatsTableWorker),
    takeLatest(inviteActions.loadMore, loadMoreWorker),
    takeLatest(inviteActions.loadInviteStatsTable, loadInviteStatsTableWorker),
    takeLatest(inviteActions.enableGold, enableGoldWorker),
    takeLatest(inviteActions.createLink, createLinkWorker),
    takeLatest(inviteActions.setStateDateRange, setStateDateRangeWorker),
    takeLatest(inviteActions.deleteLinkRequest, deleteLinkWorker),
  ]);
}
